/**
 * Hydrate les avis Yotpo (note + nombre) quand Liquid n'a pas accès aux metafields.
 */
(function () {
  if (window.__yotpoReviewsHydratorInitialized) return;
  window.__yotpoReviewsHydratorInitialized = true;

  const COUNT_SELECTOR = '[data-yotpo-count-text][data-yotpo-count-pending]';
  const RATING_TEXT_SELECTOR = '[data-yotpo-rating-text][data-yotpo-rating-text-pending]';
  const RATING_SELECTOR = '[data-yotpo-ratings][data-yotpo-ratings-pending]';
  const RETRY_DELAYS = [0, 400, 1200, 2500, 5000];

  function getYotpoAppKey() {
    const metaKey = document.querySelector('meta[name="yotpo-app-key"]')?.content;
    if (metaKey) return metaKey;

    const widget = document.querySelector('[data-appkey], [data-app-key]');
    if (widget) {
      return widget.getAttribute('data-appkey') || widget.getAttribute('data-app-key');
    }

    if (window.yotpo?.appKey) return window.yotpo.appKey;
    if (window.yotpo?.config?.appKey) return window.yotpo.config.appKey;

    const scripts = document.querySelectorAll('script[src*="yotpo.com"]');
    for (const script of scripts) {
      const patterns = [
        /loader\/([^/?]+)/,
        /staticw2\.yotpo\.com\/([^/?]+)\//,
        /widget\.yotpo\.com\/([^/?]+)/,
        /app_key=([^&]+)/i,
        /appkey=([^&]+)/i,
      ];

      for (const pattern of patterns) {
        const match = script.src.match(pattern);
        if (match?.[1]) return match[1];
      }
    }

    return null;
  }

  function parseBottomline(data) {
    if (!data || typeof data !== 'object') return { rating: null, count: null };

    const bottomline = data.bottomline || data;
    const pagination = data.pagination || {};

    const count =
      pagination.total ??
      bottomline.total_review ??
      bottomline.totalReview ??
      bottomline.total_reviews ??
      bottomline.totalReviews ??
      null;

    const rating =
      bottomline.average_score ??
      bottomline.averageScore ??
      bottomline.score ??
      null;

    return {
      rating: rating != null && Number(rating) > 0 ? Number(rating) : null,
      count: count != null && Number(count) > 0 ? Number(count) : null,
    };
  }

  function parseFromRawString(raw) {
    if (!raw || typeof raw !== 'string') return { rating: null, count: null };

    try {
      return parseBottomline(JSON.parse(raw));
    } catch {
      const countMatch =
        raw.match(/"total_review"\s*:\s*(\d+)/) ||
        raw.match(/"totalReview"\s*:\s*(\d+)/) ||
        raw.match(/"totalReviews"\s*:\s*(\d+)/) ||
        raw.match(/"total"\s*:\s*(\d+)/);
      const ratingMatch =
        raw.match(/"average_score"\s*:\s*([\d.]+)/) ||
        raw.match(/"averageScore"\s*:\s*([\d.]+)/);

      return {
        count: countMatch ? Number(countMatch[1]) : null,
        rating: ratingMatch ? Number(ratingMatch[1]) : null,
      };
    }
  }

  function getEmbeddedScript(productId, attribute) {
    const script = document.querySelector(
      `script[${attribute}][data-product-id="${productId}"]`
    );
    if (!script?.textContent?.trim()) return { rating: null, count: null };

    return parseFromRawString(script.textContent.trim());
  }

  function getEmbeddedData(productId) {
    const preloaded = getEmbeddedScript(productId, 'data-yotpo-preloaded-reviews');
    const shopifyReviews = getEmbeddedScript(productId, 'data-yotpo-shopify-reviews');

    return {
      rating: preloaded.rating || shopifyReviews.rating,
      count: preloaded.count || shopifyReviews.count,
    };
  }

  function scrapeYotpoWidget(productId) {
    const selectors = productId
      ? [
          `.yotpo.bottomLine[data-product-id="${productId}"]`,
          `.yotpo.bottomline[data-product-id="${productId}"]`,
          `[data-yotpo-product-id="${productId}"]`,
        ]
      : ['.yotpo.bottomLine', '.yotpo.bottomline'];

    for (const selector of selectors) {
      const widget = document.querySelector(selector);
      if (!widget) continue;

      const reviewText = widget.querySelector(
        '.text-m, .yotpo-review-count, .yotpo-bottomline-total-reviews, a[href*="yotpo"]'
      )?.textContent;
      const countMatch = reviewText?.replace(/\s/g, '').match(/(\d[\d.,]*)/);
      const count = countMatch ? Number(countMatch[1].replace(/[.,]/g, '')) : null;

      const ratingAttr =
        widget.querySelector('.yotpo-stars, .yotpo-icon-star')?.getAttribute('data-score') ||
        widget.getAttribute('data-score');
      const rating = ratingAttr ? Number(ratingAttr) : null;

      if ((count && count > 0) || (rating && rating > 0)) {
        return {
          rating: rating && rating > 0 ? rating : null,
          count: count && count > 0 ? count : null,
        };
      }
    }

    return { rating: null, count: null };
  }

  async function getApiData(productId) {
    const appKey = getYotpoAppKey();
    if (!appKey || !productId) return { rating: null, count: null };

    const endpoints = [
      `https://api-cdn.yotpo.com/v1/widget/${appKey}/products/${productId}/reviews.json`,
      `https://api.yotpo.com/v1/widget/${appKey}/products/${productId}/reviews.json`,
    ];

    for (const endpoint of endpoints) {
      try {
        const response = await fetch(endpoint, {
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) continue;

        const data = await response.json();
        const parsed = parseBottomline(data?.response || data);
        if (parsed.rating || parsed.count) return parsed;
      } catch {
        continue;
      }
    }

    return { rating: null, count: null };
  }

  async function resolveYotpoData(productId) {
    const embedded = getEmbeddedData(productId);
    if (embedded.rating && embedded.count) return embedded;

    const widgetData = scrapeYotpoWidget(productId);
    const merged = {
      rating: embedded.rating || widgetData.rating,
      count: embedded.count || widgetData.count,
    };
    if (merged.rating && merged.count) return merged;

    const apiData = await getApiData(productId);
    return {
      rating: merged.rating || apiData.rating,
      count: merged.count || apiData.count,
    };
  }

  function buildStar(fill) {
    const star = document.createElement('span');
    star.className = 'rating-star';
    star.style.setProperty('--star-fill', `${fill}%`);
    star.setAttribute('aria-hidden', 'true');
    return star;
  }

  function renderStars(container, rating) {
    if (!container || !rating || rating <= 0) return;

    container.replaceChildren();

    for (let i = 1; i <= 5; i += 1) {
      const fill = Math.min(100, Math.max(0, (rating - (i - 1)) * 100));
      container.appendChild(buildStar(fill));
    }
  }

  function formatRating(rating) {
    const rounded = Math.round(Number(rating) * 10) / 10;
    return Number.isInteger(rounded) ? String(Math.round(rounded)) : String(rounded);
  }

  function applyCountTemplate(element, template, count) {
    if (!count || count <= 0 || !template) return false;

    element.innerHTML = template.replace(/\[count\]/g, String(count));
    element.removeAttribute('data-yotpo-count-pending');
    return true;
  }

  function applyRatingTextTemplate(element, template, rating) {
    if (!rating || rating <= 0 || !template) return false;

    const formatted = formatRating(rating);
    let html = template.replace(/\[rating\]/g, String(formatted));

    if (!template.includes('[rating]')) {
      html = `${formatted}${template}`;
    }

    element.innerHTML = html;
    element.removeAttribute('data-yotpo-rating-text-pending');
    return true;
  }

  function updateRatingBlock(element, data) {
    const { rating, count } = data;
    const showNote = element.dataset.showNote === 'true';
    const needsRating = element.dataset.needsRating !== 'false';
    const needsCount = element.dataset.needsCount === 'true';
    let updated = false;

    if (needsRating && rating && rating > 0) {
      const starsContainer = element.querySelector('.rating__stars, .rating-stars');
      renderStars(starsContainer, rating);

      const ratingRoot = element.querySelector('.rating') || element;
      ratingRoot.dataset.ratingValue = String(rating);

      if (showNote) {
        const note = element.querySelector('.rating__note');
        if (note) note.textContent = `${formatRating(rating)}/5`;
      }

      updated = true;
    }

    if (needsCount && count && count > 0) {
      const countEl = element.querySelector('.rating__count');
      if (countEl) {
        const labelMatch = countEl.textContent.match(/\(\s*\d[\d.,]*\s*(.*)\)/);
        const label = labelMatch?.[1]?.trim() || 'avis';
        countEl.textContent = `(${count} ${label})`;
      }
      updated = true;
    }

    if (updated) {
      const stillNeedsRating = needsRating && !(rating && rating > 0);
      const stillNeedsCount = needsCount && !(count && count > 0);
      if (!stillNeedsRating && !stillNeedsCount) {
        element.removeAttribute('data-yotpo-ratings-pending');
      }
    }
  }

  async function updateRatingTextElement(element) {
    const productId = element.dataset.productId;
    const template = element.dataset.yotpoTemplate;
    if (!productId || !template) return false;

    const data = await resolveYotpoData(productId);
    return applyRatingTextTemplate(element, template, data.rating);
  }

  async function updateCountElement(element) {
    const productId = element.dataset.productId;
    const template = element.dataset.yotpoTemplate;
    if (!productId || !template) return false;

    const data = await resolveYotpoData(productId);
    return applyCountTemplate(element, template, data.count);
  }

  async function updateRatingElement(element) {
    const productId = element.dataset.productId;
    if (!productId) return false;

    const data = await resolveYotpoData(productId);
    if (!data.rating && !data.count) return false;

    updateRatingBlock(element, data);
    return true;
  }

  async function hydratePending() {
    const countElements = document.querySelectorAll(COUNT_SELECTOR);
    const ratingTextElements = document.querySelectorAll(RATING_TEXT_SELECTOR);
    const ratingElements = document.querySelectorAll(RATING_SELECTOR);

    await Promise.all([
      ...Array.from(countElements).map(updateCountElement),
      ...Array.from(ratingTextElements).map(updateRatingTextElement),
      ...Array.from(ratingElements).map(updateRatingElement),
    ]);
  }

  async function init() {
    for (const delay of RETRY_DELAYS) {
      if (delay) await new Promise((resolve) => setTimeout(resolve, delay));

      await hydratePending();

      const stillPending = document.querySelector(
        '[data-yotpo-count-pending], [data-yotpo-rating-text-pending], [data-yotpo-ratings-pending]'
      );
      if (!stillPending) break;
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.addEventListener('load', () => {
    setTimeout(init, 800);
  });

  document.addEventListener('shopify:section:load', init);
  document.addEventListener('yotpo:ready', init);
})();
